import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Artist, Project, ProjectStatus, SortOption } from '../types';
import { API_CONFIG } from '../config';
import ArtistCard from './ArtistCard';
import Modal from './ui/Modal';
import ConfirmationModal from './ui/ConfirmationModal';
import ContextMenu, { ContextMenuItem } from './ContextMenu';
import { useTranslation } from '../hooks/useTranslation';
import { getAvatarUrl, getInitials, getAvatarColor } from '../utils/avatarUtils';
import { ICONS } from '../constants';

const getStatusTranslationKey = (status: ProjectStatus): string => {
  const statusMap: Record<ProjectStatus, string> = {
    'Planning': 'planning',
    'In Progress': 'inProgress',
    'On Hold': 'onHold',
    'Completed': 'completed',
    'Cancelled': 'cancelled',
    'MagicBox': 'magicbox',
  };
  return statusMap[status] || status.toLowerCase();
};

const isProjectActive = (project: Project): boolean => {
  try {
    if (project.status === 'Completed' || project.status === 'Cancelled') {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = new Date(project.startDate);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(project.endDate);
    endDate.setHours(23, 59, 59, 999);

    return today >= startDate && today <= endDate;
  } catch {
    return false;
  }
};

interface ProjectWithArtists extends Project {
  assignedArtists: Artist[];
}

interface TeamsViewProps {
  projects: Project[];
  artists: Artist[];
  currentRole: string | null;
  onEditArtist: (artist: Artist) => void;
  onDeleteArtist: (artistId: string) => void;
  onAddUsersToProject?: (project: Project) => void;
  onAssignToProject?: (artistIds: string[], projectId: string) => Promise<void>;
  onRemoveFromProject?: (projectId: string, artistId: string) => void;
  sortOption?: SortOption;
  filters?: {
    name?: string;
    status?: string;
    priority?: string;
    specialization?: string;
  };
}

const CompactAvatar: React.FC<{ artist: Artist; size?: 'sm' | 'md' }> = ({ artist, size = 'md' }) => {
  const avatarUrl = getAvatarUrl(artist.avatarUrl);
  const initials = getInitials(artist.name);
  const avatarColor = getAvatarColor(artist.name);
  const sizeClass = size === 'sm' ? 'w-6 h-6 text-[10px]' : 'w-9 h-9 text-xs';

  return (
    <>
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={artist.name}
          className={`${sizeClass} rounded-full object-cover flex-shrink-0`}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const fallback = target.nextElementSibling as HTMLElement;
            if (fallback) fallback.style.display = 'flex';
          }}
        />
      ) : null}
      <div
        className={`${sizeClass} rounded-full flex items-center justify-center font-bold text-white flex-shrink-0 ${avatarColor}`}
        style={{ display: avatarUrl ? 'none' : 'flex' }}
      >
        {initials}
      </div>
    </>
  );
};

const TeamsView: React.FC<TeamsViewProps> = ({
  projects,
  artists,
  currentRole,
  onEditArtist,
  onDeleteArtist,
  onAddUsersToProject,
  onAssignToProject,
  onRemoveFromProject,
  sortOption = SortOption.NAME_ASC,
  filters = {},
}) => {
  const { t } = useTranslation();
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [isArtistModalOpen, setIsArtistModalOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; project: Project } | null>(null);
  const [showArtistsList, setShowArtistsList] = useState<{
    isOpen: boolean;
    type: 'active' | 'all';
  }>({
    isOpen: false,
    type: 'all',
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    projectId: string | null;
    artistId: string | null;
    artistName: string | null;
    projectName: string | null;
  }>({
    isOpen: false,
    projectId: null,
    artistId: null,
    artistName: null,
    projectName: null,
  });
  const [selectedArtistIds, setSelectedArtistIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const [dragOverProjectId, setDragOverProjectId] = useState<string | null>(null);

  const projectsWithArtists = useMemo(() => {
    const withArtists = projects.map(project => ({
      ...project,
      assignedArtists: artists.filter(a => a.assignedProjects?.includes(project.id)),
    }));
    const sorted = [...withArtists];
    switch (sortOption) {
      case SortOption.NAME_ASC:
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case SortOption.NAME_DESC:
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      default:
        return sorted;
    }
  }, [projects, artists, sortOption]);

  const filteredArtists = useMemo(() => {
    let result = artists;
    if (filters.specialization) {
      result = result.filter(a => a.specialization === filters.specialization);
    }
    const sorted = [...result];
    switch (sortOption) {
      case SortOption.NAME_ASC:
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case SortOption.NAME_DESC:
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      default:
        return sorted;
    }
  }, [artists, filters.specialization, sortOption]);

  const canEdit = currentRole && ['Super Admin', 'Admin', 'Producer', 'Project Manager', 'Coordinator'].includes(currentRole);
  const hasFullAccess = currentRole === 'Admin' || currentRole === 'Producer' || currentRole === 'Super Admin';

  const handleArtistClick = (artist: Artist) => {
    setSelectedArtist(artist);
    setIsArtistModalOpen(true);
  };

  const handleCloseArtistModal = () => {
    setIsArtistModalOpen(false);
    setSelectedArtist(null);
  };

  const handleProfileSelect = (artistId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const newSelectedIds = new Set(selectedArtistIds);

    if (event.shiftKey && lastSelectedId) {
      const lastIndex = filteredArtists.findIndex(a => a.id === lastSelectedId);
      const currentIndex = filteredArtists.findIndex(a => a.id === artistId);
      if (lastIndex !== -1 && currentIndex !== -1) {
        const start = Math.min(lastIndex, currentIndex);
        const end = Math.max(lastIndex, currentIndex);
        for (let i = start; i <= end; i++) {
          newSelectedIds.add(filteredArtists[i].id);
        }
      }
    } else if (newSelectedIds.has(artistId)) {
      newSelectedIds.delete(artistId);
    } else {
      newSelectedIds.add(artistId);
    }

    setSelectedArtistIds(newSelectedIds);
    setLastSelectedId(artistId);
  };

  const handleProfileDragStart = (e: React.DragEvent<HTMLDivElement>, artistId: string) => {
    if (!canEdit) {
      e.preventDefault();
      return;
    }

    const idsToDrag =
      selectedArtistIds.has(artistId) && selectedArtistIds.size > 0
        ? Array.from(selectedArtistIds)
        : [artistId];

    e.dataTransfer.setData('application/json', JSON.stringify({ artistIds: idsToDrag }));
    e.dataTransfer.effectAllowed = 'copy';

    const el = e.currentTarget;
    const ghost = el.cloneNode(true) as HTMLElement;
    ghost.style.position = 'absolute';
    ghost.style.top = '-9999px';
    ghost.style.width = `${el.offsetWidth}px`;
    ghost.style.opacity = '0.85';
    ghost.style.backgroundColor = 'rgb(55, 65, 81)';
    ghost.style.borderRadius = '0.5rem';
    if (idsToDrag.length > 1) {
      const badge = document.createElement('div');
      badge.textContent = `×${idsToDrag.length}`;
      badge.style.cssText =
        'position:absolute;top:-8px;right:-8px;background:rgb(99,102,241);color:white;border-radius:9999px;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:bold;';
      ghost.style.position = 'relative';
      ghost.appendChild(badge);
    }
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, el.offsetWidth / 2, el.offsetHeight / 2);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const handleProjectDragOver = (e: React.DragEvent<HTMLDivElement>, projectId: string) => {
    if (!canEdit) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverProjectId(projectId);
  };

  const handleProjectDragLeave = (e: React.DragEvent<HTMLDivElement>, projectId: string) => {
    const related = e.relatedTarget as Node | null;
    if (related && e.currentTarget.contains(related)) return;
    if (dragOverProjectId === projectId) {
      setDragOverProjectId(null);
    }
  };

  const handleProjectDrop = async (e: React.DragEvent<HTMLDivElement>, project: ProjectWithArtists) => {
    e.preventDefault();
    setDragOverProjectId(null);
    if (!canEdit || !onAssignToProject) return;

    try {
      const raw = e.dataTransfer.getData('application/json');
      if (!raw) return;
      const { artistIds } = JSON.parse(raw) as { artistIds: string[] };
      if (!artistIds?.length) return;

      const newIds = artistIds.filter(
        id => !project.assignedArtists.some(a => a.id === id)
      );
      if (newIds.length === 0) return;

      await onAssignToProject(newIds, project.id);
      setSelectedArtistIds(new Set());
      setLastSelectedId(null);
    } catch (error) {
      console.error('Error assigning artists via drag-and-drop:', error);
    }
  };

  const handleRemoveArtistFromProject = (projectId: string, artistId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const project = projectsWithArtists.find(p => p.id === projectId);
    const artist = project?.assignedArtists.find(a => a.id === artistId);

    if (project && artist) {
      setDeleteConfirmation({
        isOpen: true,
        projectId,
        artistId,
        artistName: artist.name,
        projectName: project.name,
      });
    }
  };

  const handleConfirmRemove = async () => {
    if (!deleteConfirmation.projectId || !deleteConfirmation.artistId) return;

    try {
      const token = localStorage.getItem('spellbook_token');
      if (!token) return;

      const response = await fetch(
        `${API_CONFIG.PROJECTS.UPDATE(deleteConfirmation.projectId)}/artists/${deleteConfirmation.artistId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        onRemoveFromProject?.(deleteConfirmation.projectId, deleteConfirmation.artistId);
        setDeleteConfirmation({
          isOpen: false,
          projectId: null,
          artistId: null,
          artistName: null,
          projectName: null,
        });
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Ошибка при удалении художника из проекта');
      }
    } catch (error) {
      console.error('Error removing artist from project:', error);
      alert('Ошибка при удалении художника из проекта');
    }
  };

  const handleCancelRemove = () => {
    setDeleteConfirmation({
      isOpen: false,
      projectId: null,
      artistId: null,
      artistName: null,
      projectName: null,
    });
  };

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);

  const handleProjectContextMenu = (e: React.MouseEvent, project: Project) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, project });
  };

  const getContextMenuItems = (project: Project): ContextMenuItem[] => {
    const isDisabled = hasFullAccess ? false : !onAddUsersToProject || !canEdit;

    return [
      {
        label: t('contextMenu.addUsersToProject') || 'Add Users to Project',
        icon: <ICONS.UserPlus className="w-4 h-4" />,
        onClick: () => {
          onAddUsersToProject?.(project);
          setContextMenu(null);
        },
        disabled: isDisabled,
      },
    ];
  };

  const getStatusBadgeClass = (status: ProjectStatus) => {
    switch (status) {
      case 'In Progress':
      case 'Completed':
        return 'bg-blue-500/20 text-blue-400';
      case 'On Hold':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'Cancelled':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const stats = useMemo(() => {
    const uniqueArtistIds = new Set<string>();
    projectsWithArtists.forEach(project => {
      project.assignedArtists.forEach(artist => uniqueArtistIds.add(artist.id));
    });

    const activeArtistIds = new Set<string>();
    uniqueArtistIds.forEach(artistId => {
      const artistProjects = projectsWithArtists.filter(project =>
        project.assignedArtists.some(artist => artist.id === artistId)
      );
      if (artistProjects.some(project => isProjectActive(project))) {
        activeArtistIds.add(artistId);
      }
    });

    const allArtistsMap = new Map<string, { artist: Artist; projects: Project[] }>();
    const activeArtistsMap = new Map<string, { artist: Artist; projects: Project[] }>();

    uniqueArtistIds.forEach(artistId => {
      const artist = projectsWithArtists.flatMap(p => p.assignedArtists).find(a => a.id === artistId);
      if (artist) {
        const artistProjects = projectsWithArtists.filter(project =>
          project.assignedArtists.some(a => a.id === artistId)
        );
        allArtistsMap.set(artistId, { artist, projects: artistProjects });
        if (activeArtistIds.has(artistId)) {
          activeArtistsMap.set(artistId, { artist, projects: artistProjects });
        }
      }
    });

    return {
      total: uniqueArtistIds.size,
      active: activeArtistIds.size,
      allArtists: Array.from(allArtistsMap.values()),
      activeArtists: Array.from(activeArtistsMap.values()),
    };
  }, [projectsWithArtists]);

  const handlePanelBackgroundClick = useCallback((e: React.MouseEvent) => {
    if (!(e.target as HTMLElement).closest('[data-profile-tile]')) {
      setSelectedArtistIds(new Set());
      setLastSelectedId(null);
    }
  }, []);

  return (
    <>
      <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ICONS.Users className="w-5 h-5 text-indigo-400" />
            <div>
              <div className="text-sm text-gray-400">Статистика команды</div>
              <div className="text-lg font-semibold text-white">
                <button
                  onClick={() => setShowArtistsList({ isOpen: true, type: 'active' })}
                  className="text-green-400 hover:text-green-300 hover:underline transition-colors"
                  title="Показать активных художников"
                >
                  {stats.active}
                </button>
                <span className="text-gray-500 mx-1">из</span>
                <button
                  onClick={() => setShowArtistsList({ isOpen: true, type: 'all' })}
                  className="text-white hover:text-gray-300 hover:underline transition-colors"
                  title="Показать всех художников"
                >
                  {stats.total}
                </button>
                <span className="text-gray-400 text-sm ml-2">
                  {stats.total === 1 ? 'художник активен' : stats.total < 5 ? 'художника активны' : 'художников активны'}
                </span>
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            {projectsWithArtists.length}{' '}
            {projectsWithArtists.length === 1 ? 'проект' : projectsWithArtists.length < 5 ? 'проекта' : 'проектов'}
          </div>
        </div>
      </div>

      <Modal
        isOpen={showArtistsList.isOpen}
        onClose={() => setShowArtistsList({ isOpen: false, type: 'all' })}
        title={showArtistsList.type === 'active' ? 'Активные художники' : 'Все художники'}
        size="lg"
      >
        <div className="max-h-96 overflow-y-auto">
          <div className="space-y-2">
            {(showArtistsList.type === 'active' ? stats.activeArtists : stats.allArtists).map(({ artist, projects: artistProjects }) => {
              const isActiveArtist = stats.activeArtists.some(a => a.artist.id === artist.id);
              const activeProjects = artistProjects.filter(p => isProjectActive(p));

              return (
                <div
                  key={artist.id}
                  onClick={() => {
                    setSelectedArtist(artist);
                    setIsArtistModalOpen(true);
                    setShowArtistsList({ isOpen: false, type: 'all' });
                  }}
                  className="flex items-center space-x-3 p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-all cursor-pointer border border-gray-600 hover:border-indigo-500/50"
                >
                  <CompactAvatar artist={artist} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <div className="text-white font-medium truncate">{artist.name}</div>
                      {isActiveArtist && (
                        <span className="px-2 py-0.5 text-xs rounded bg-green-500/20 text-green-400">Активен</span>
                      )}
                    </div>
                    {artist.specialization && (
                      <div className="text-sm text-gray-400 truncate">{artist.specialization}</div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                      {activeProjects.length > 0 && (
                        <span className="text-green-400">
                          {activeProjects.length}{' '}
                          {activeProjects.length === 1 ? 'активный проект' : activeProjects.length < 5 ? 'активных проекта' : 'активных проектов'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {(showArtistsList.type === 'active' ? stats.activeArtists : stats.allArtists).length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <ICONS.Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {showArtistsList.type === 'active' ? 'Нет активных художников' : 'Нет художников'}
                </p>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: projects */}
        <div className="lg:col-span-1 space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto pr-1">
          <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide px-1">
            {t('contentView.projects')}
          </h3>
          {projectsWithArtists.length === 0 ? (
            <div className="text-center text-gray-500 py-12 px-4 bg-gray-800/50 rounded-lg border border-gray-700 border-dashed">
              <p className="text-sm">{t('teams.noProjects')}</p>
            </div>
          ) : (
            projectsWithArtists.map(project => {
              const visibleArtists = project.assignedArtists.slice(0, 3);
              const hiddenCount = project.assignedArtists.length - visibleArtists.length;
              const isDropTarget = dragOverProjectId === project.id;

              return (
                <div
                  key={project.id}
                  className={`bg-gray-800 rounded-lg p-3 border transition-all cursor-context-menu ${
                    isDropTarget
                      ? 'border-indigo-400 ring-2 ring-indigo-400 bg-indigo-500/10'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                  onContextMenu={e => handleProjectContextMenu(e, project)}
                  onDragOver={e => handleProjectDragOver(e, project.id)}
                  onDragLeave={e => handleProjectDragLeave(e, project.id)}
                  onDrop={e => handleProjectDrop(e, project)}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {project.color && (
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: project.color }} />
                      )}
                      <h4 className="text-sm font-semibold text-white truncate">{project.name}</h4>
                    </div>
                    <span className="text-xs text-gray-500 flex-shrink-0">
                      {t('teams.assignedCount', { count: project.assignedArtists.length })}
                    </span>
                  </div>

                  {project.status && (
                    <span className={`inline-block px-2 py-0.5 text-[10px] rounded mb-2 ${getStatusBadgeClass(project.status)}`}>
                      {t(`projectStatus.${getStatusTranslationKey(project.status)}`)}
                    </span>
                  )}

                  {isDropTarget && canEdit && (
                    <div className="text-xs text-indigo-300 mb-2">{t('teams.dropToAssign')}</div>
                  )}

                  {project.assignedArtists.length === 0 ? (
                    <div className="text-xs text-gray-500 py-2 border-t border-gray-700/50 mt-2">
                      {canEdit ? t('teams.dropToAssign') : '—'}
                    </div>
                  ) : (
                    <div className="space-y-1.5 border-t border-gray-700/50 pt-2 mt-2">
                      {visibleArtists.map(artist => (
                        <div
                          key={artist.id}
                          onClick={() => handleArtistClick(artist)}
                          className="flex items-center gap-2 group cursor-pointer hover:bg-gray-700/40 rounded px-1 py-0.5"
                        >
                          <CompactAvatar artist={artist} size="sm" />
                          <span className="text-xs text-gray-300 truncate flex-1">{artist.name}</span>
                          {canEdit && (
                            <button
                              onClick={e => handleRemoveArtistFromProject(project.id, artist.id, e)}
                              className="opacity-0 group-hover:opacity-100 w-4 h-4 flex items-center justify-center text-red-400 hover:text-red-300 flex-shrink-0"
                              title="Удалить из проекта"
                            >
                              <ICONS.X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                      {hiddenCount > 0 && (
                        <div className="text-xs text-gray-500 px-1">+{hiddenCount}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Right: profiles */}
        <div
          className="lg:col-span-2 max-h-[calc(100vh-280px)] overflow-y-auto"
          onClick={handlePanelBackgroundClick}
        >
          <div className="flex items-center justify-between mb-3 px-1">
            <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide">
              {t('contentView.artists')}
            </h3>
            {canEdit && (
              <p className="text-xs text-gray-500">{t('teams.dragHint')}</p>
            )}
          </div>

          {selectedArtistIds.size > 0 && (
            <div className="mb-3 px-3 py-2 bg-indigo-500/10 border border-indigo-500/30 rounded-lg text-sm text-indigo-300">
              {t('teams.draggingCount', { count: selectedArtistIds.size })}
            </div>
          )}

          {filteredArtists.length === 0 ? (
            <div className="text-center text-gray-500 py-12 px-4 bg-gray-800/50 rounded-lg border border-gray-700 border-dashed">
              <p className="text-sm">{t('teams.noProfiles')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredArtists.map(artist => {
                const isSelected = selectedArtistIds.has(artist.id);
                const projectCount = artist.assignedProjects?.length || 0;

                return (
                  <div
                    key={artist.id}
                    data-profile-tile
                    draggable={!!canEdit}
                    onDragStart={e => handleProfileDragStart(e, artist.id)}
                    onClick={e => handleProfileSelect(artist.id, e)}
                    className={`rounded-lg p-3 border transition-all select-none ${
                      canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                    } ${
                      isSelected
                        ? 'bg-indigo-500/10 border-indigo-500 ring-2 ring-indigo-500'
                        : 'bg-gray-800 border-gray-700 hover:border-gray-600 hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <CompactAvatar artist={artist} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-white truncate">{artist.name}</div>
                        {artist.specialization && (
                          <div className="text-xs text-gray-400 truncate">{artist.specialization}</div>
                        )}
                      </div>
                    </div>
                    {projectCount > 0 && (
                      <div className="text-[10px] text-gray-500 mt-1">
                        {projectCount}{' '}
                        {projectCount === 1 ? 'проект' : projectCount < 5 ? 'проекта' : 'проектов'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        title="Удалить художника из проекта?"
        message={`Вы уверены, что хотите удалить "${deleteConfirmation.artistName}" из проекта "${deleteConfirmation.projectName}"? Художник останется в сметах, но будет удалён из проекта.`}
        confirmText="Удалить"
        cancelText="Отмена"
        type="warning"
        itemName={deleteConfirmation.artistName || undefined}
      />

      {selectedArtist && (
        <Modal
          isOpen={isArtistModalOpen}
          onClose={handleCloseArtistModal}
          title={selectedArtist.name}
          size="lg"
        >
          <div className="mt-4">
            <ArtistCard
              artist={selectedArtist}
              projects={projects}
              currentRole={currentRole}
              onEdit={onEditArtist}
              onDelete={undefined}
              isSelected={false}
              onSelect={() => {}}
            />
          </div>
        </Modal>
      )}

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={getContextMenuItems(contextMenu.project)}
          onClose={() => setContextMenu(null)}
        />
      )}
    </>
  );
};

export default TeamsView;
